import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Sun, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { TID } from "@/constants/testIds";

const links = [
  { to: "/events", label: "Events", tid: TID.navEvents },
  { to: "/experiences", label: "Experiences", tid: TID.navExperiences },
  { to: "/directory", label: "Directory", tid: TID.navDirectory },
  { to: "/guides", label: "Guides", tid: TID.navGuides },
  { to: "/concierge", label: "Concierge", tid: TID.navConcierge },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40">
      <div className="glass-card border-b border-[color:var(--line)]">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
          <Link to="/" data-testid={TID.navLogo} className="flex items-center gap-2 group">
            <Sun size={22} className="text-[color:var(--mango)] group-hover:rotate-45 transition-transform duration-500" />
            <div className="flex items-baseline gap-1">
              <span className="font-display text-[1.6rem] font-bold leading-none text-[color:var(--ocean)]">Hey</span>
              <span className="font-hand text-[1.9rem] font-bold leading-none text-[color:var(--coral)]">GC</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.tid}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="btn-pill btn-ghost text-[color:var(--coral)]">
                    <ShieldCheck size={15} /> Admin
                  </Link>
                )}
                <Link to="/dashboard" data-testid={TID.navDashboard} className="btn-pill btn-outline">
                  <User size={15} /> {user.name?.split(" ")[0] || "Account"}
                </Link>
                <button onClick={doLogout} data-testid={TID.navLogout} className="btn-pill btn-ghost">
                  <LogOut size={15} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid={TID.navLogin} className="btn-pill btn-ghost">
                  Sign in
                </Link>
                <Link to="/register" data-testid={TID.navSignup} className="btn-pill btn-primary">
                  Join the crew
                </Link>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            data-testid={TID.navMobileToggle}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[color:var(--line)] px-6 py-4 bg-[color:var(--shell)]/95 backdrop-blur-xl">
            <div className="flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`${l.tid}-mobile`}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""} text-lg`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="pt-4 mt-2 border-t border-[color:var(--line)] flex gap-3">
                {user ? (
                  <>
                    {user.role === "admin" && (
                      <Link to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin-mobile" className="btn-pill btn-ghost">Admin</Link>
                    )}
                    <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-pill btn-outline flex-1 justify-center">
                      Account
                    </Link>
                    <button onClick={() => { setOpen(false); doLogout(); }} className="btn-pill btn-ghost">
                      <LogOut size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)} className="btn-pill btn-outline flex-1 justify-center">
                      Sign in
                    </Link>
                    <Link to="/register" onClick={() => setOpen(false)} className="btn-pill btn-primary flex-1 justify-center">
                      Join
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
